import { inject } from '@angular/core';
import { ActivityEntityName, UPCOMING_WORK_CSV_COMMAND_ID, UpcomingWorkCsvExport } from '@jms/model';
import { clientPaths, PortalClientAuthorizeGuard } from '@jms/tenant/module.jms.authorisation';
import { EntityDataService } from '@workbuddy/services/entities';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board';
import { saveAs } from 'file-saver';

export interface ClientCsvDownload {
  entity: ActivityEntityName;
  /**
   * The portal path whose permission gates the board, so the button shows exactly where the board does.
   * Omitted on a tenant board, where the entity's own read right gates both the board and the export.
   */
  path?: clientPaths;
  commandId: string;
  /** The board's name, for the failure toast. */
  title: string;
}

/**
 * A board's Download CSV: the board's current request goes to the entity's CSV command and the file the
 * API built is saved as is. Call in an injection context (a board's `primaryCommands`).
 */
export function clientCsvDownloadCommands(download: ClientCsvDownload): Command[] {

  if (download.path && !inject(PortalClientAuthorizeGuard).canLoadPath(download.path)) return [];

  const store = inject(EntityBoardStore);
  const data = inject(EntityDataService);
  const toast = inject(ToastService);
  return [new Command({
    _id: download.commandId,
    label: 'Download CSV',
    iconLeft: 'fa-regular fa-file-csv',
    action: async () => {
      const request = store.$itemsRequest();
      if (!request) return;

      try {
        const result = await data.executeCommand<UpcomingWorkCsvExport>(download.entity, UPCOMING_WORK_CSV_COMMAND_ID, request);
        saveAs(new Blob(['﻿', result.content], { type: result.contentType }), result.filename);
      } catch {
        toast.error(download.title, `Unable to download ${download.title} CSV`);
      }
    },
  })];
}
